import { AiOutlineInteraction } from 'react-icons/ai'
import { IoHomeOutline } from 'react-icons/io5'
import { MdAddchart, MdOutlineGavel } from 'react-icons/md'
import { TbFileCheck, TbFolderCog } from 'react-icons/tb'

import { MenuBadgeIcon, MenuBadgeLabel } from '@/Ship/Components/MenuBadge'

import type { MenuItems } from '@/Core/Contracts/UI/Menu'

/**
 * Пункт «Диск» у меню — лише за `VITE_DISK_ENABLED=true` (54631). Та сама
 * змінна закриває сторінки диска (`DiskSection/DiskContainer/Helpers/diskFeature`).
 */
const DISK_MENU_ENABLED = import.meta.env['VITE_DISK_ENABLED'] === 'true'

/**
 * Бокове меню (`SPEC-014` §1.3): порядок і підписи легасі (`Menu.jsx:49-184`),
 * у кожного пункту своя іконка (вартовий в `architectureGuard.test.ts`).
 *
 * Група «Перевезення» (`SPEC-025` §3.4.3, етап 2) — після CRM, без «Звітів»
 * (Q2.1). Іконки — ті самі, що в шапках сторінок (`pageConfig` ресурсів).
 *
 * Група «Звіти» (`SPEC-027` §3.4, К-01, етап 3) — між «Перевезення» і
 * «Завдання», як у легасі (`Menu.jsx:119-131`); бачать усі (Q3.2), тож у
 * `GATED_MENU_ITEMS` її немає. Іконка — `pageConfig` сторінки `Report.Index`.
 *
 * Група «Завдання» — модуль завдань з демки (рішення замовника 2026-10-15,
 * `SPEC-018`): ті самі чотири пункти, підписи й іконки (з `pageConfig`
 * сторінок), що в демці. У демці група стояла одразу після «На головну»;
 * тут — після CRM, бо CRM — головний розділ Астерікса.
 */
export const Menu: MenuItems = [
    // Об'єкт, а не рядок: рядковий ключ бере підпис із `pageConfig.title`
    // сторінки, а там заголовок «Робочого столу» (`SPEC-015`). У меню легасі —
    // «На головну» (`Menu.jsx:51`). Клік ядро веде за `key` (`useMenuNavigation`)
    { key: 'Index', label: 'На головну', icon: <IoHomeOutline /> },
    {
        key: 'crm',
        label: 'CRM',
        type: 'group',
        // «Справи» — першими в групі, як у легасі (`SPEC-002` §2.3). Об'єкт, а
        // не рядок: підпис і іконка несуть бейдж прострочених справ (ADR 030,
        // `SPEC-014` §1.3). Ядро лишає об'єктний пункт як є, а клік шукає
        // маршрут за `key`; підпис і іконка — ті самі, що в ресурсі
        children: [
            {
                key: 'Crm.Activity',
                label: <MenuBadgeLabel counter="activity">Справи</MenuBadgeLabel>,
                icon: (
                    <MenuBadgeIcon counter="activity">
                        <AiOutlineInteraction />
                    </MenuBadgeIcon>
                ),
            },
            'Crm.Client',
            'Crm.Carrier',
            'Crm.OtherCarrier',
        ],
    },
    {
        key: 'transportation',
        label: 'Перевезення',
        type: 'group',
        // Бейджі — записи в стані «Новий» без моєї пропозиції (`SPEC-024`
        // §5.5; лише логістам, решті — нуль і бейджа немає). «Рейси» бейджа
        // не мають (легасі `TripPage.jsx:22`) — рядок бере підпис і іконку з
        // `pageConfig`
        children: [
            {
                key: 'Transportation.Application',
                label: <MenuBadgeLabel counter="applications">Заявки</MenuBadgeLabel>,
                icon: (
                    <MenuBadgeIcon counter="applications">
                        <TbFileCheck />
                    </MenuBadgeIcon>
                ),
            },
            'Transportation.Trip',
            {
                key: 'Transportation.Request',
                label: <MenuBadgeLabel counter="requests">Запити</MenuBadgeLabel>,
                icon: (
                    <MenuBadgeIcon counter="requests">
                        <MdAddchart />
                    </MenuBadgeIcon>
                ),
            },
            {
                key: 'Transportation.Tender',
                label: <MenuBadgeLabel counter="tenders">Тендер</MenuBadgeLabel>,
                icon: (
                    <MenuBadgeIcon counter="tenders">
                        <MdOutlineGavel />
                    </MenuBadgeIcon>
                ),
            },
        ],
    },
    {
        key: 'reports',
        label: 'Звіти',
        type: 'group',
        children: ['Report.Index'],
    },
    {
        key: 'tasks',
        label: 'Завдання',
        type: 'group',
        children: ['Task.My', 'Task.Assigned', 'Task.All', 'Task.Removed'],
    },
    // «Диск» (54631, `DISK-001` §6) — бачать усі авторизовані: доступ до
    // просторів вирішує бек, а «Мій диск» є в кожного. Іконка — `pageConfig`
    // сторінки `Disk.Index`. Поки модуль не показують клієнтам, диск
    // вмикається змінною `VITE_DISK_ENABLED=true` (на дев-стенді — `.env.local`);
    // без неї немає ні пункту, ні сторінок.
    //
    // «Адміністрування диска» (`DISK-006` §5.1) — лише з правом `DiskAdmin`
    // (`GATED_MENU_ITEMS`), з бейджем справ звільнення, що чекають рішення
    // (лічильник `disk_offboarding`). Іконка — та сама, що в `pageConfig`
    // сторінки `Disk.Admin`
    ...(DISK_MENU_ENABLED
        ? [
              {
                  key: 'disk',
                  label: 'Диск',
                  type: 'group' as const,
                  children: [
                      'Disk.Index',
                      {
                          key: 'Disk.Admin',
                          label: <MenuBadgeLabel counter="disk_offboarding">Адміністрування диска</MenuBadgeLabel>,
                          icon: (
                              <MenuBadgeIcon counter="disk_offboarding">
                                  <TbFolderCog />
                              </MenuBadgeIcon>
                          ),
                      },
                  ],
              },
          ]
        : []),
    {
        key: 'team',
        label: 'Команда',
        type: 'group',
        children: ['Setting.Department', 'Setting.User'],
    },
    {
        key: 'settings',
        label: 'Налаштування',
        type: 'group',
        children: ['Setting.Index'],
    },
]
